/**
 * `useCreateFolder` mutation hook.
 *
 * Wraps the raw `createProjectFolder` service fn. Cache
 * invalidation: busts the whole `['project', projectId, 'folders']` prefix, the
 * same breadth the delete, update and thumbnail mutations use. A narrower key
 * leaves the root list and the parent's `foldersCount` stale, and that count
 * gates both the subfolder list and the row's expand chevron.
 *
 * Side effects: NONE. Toast/redirect side effects belong to the caller (the
 * modal navigates to the new folder on success; an admin tool would do
 * differently).
 */
import type {
  CreateFolderDto,
  FolderResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BRIEFS_QUERY_KEYS } from '@/entities/brief';
import { PROJECTS_QUERY_KEYS } from '@/entities/project';
import { PROJECT_TAG_QUERY_KEYS } from '@/entities/project-tag';
import { createProjectFolder } from './project-folders.api';

export { createProjectFolder };

interface UseCreateFolderOptions {
  projectId: string;
}

interface CreateFolderVariables {
  data: CreateFolderDto;
}

export function useCreateFolder(options: UseCreateFolderOptions) {
  const queryClient = useQueryClient();
  const { projectId } = options;

  return useMutation<FolderResponseDto, Error, CreateFolderVariables>({
    mutationFn: ({ data }) => createProjectFolder(projectId, data),
    onSuccess: (_data, { data }) => {
      void queryClient.invalidateQueries({
        queryKey: PROJECTS_QUERY_KEYS.folders.list(projectId).slice(0, 3),
      });
      // Tags may have been minted server-side via findOrCreateByNames; refresh
      // the workspace-wide tag pool so suggestions stay current across surfaces.
      if (data.tags?.length) {
        void queryClient.invalidateQueries({ queryKey: PROJECT_TAG_QUERY_KEYS.all });
      }
      // The server links the brief inside the create transaction, and the brief
      // side reports the folders it is delivered into, so it is describing a
      // link it has never seen until it refetches.
      if (data.briefId) {
        void queryClient.invalidateQueries({ queryKey: BRIEFS_QUERY_KEYS.all });
      }
    },
  });
}
